import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DashboardHeaderProps } from '../../types/dashboard';
import { artistAnalyticsService, UserArtist } from '../../services/api/artist-analytics.service';
import { useUser } from "contexts/UserContext";
import { useArtist } from '../../contexts/ArtistContext';


const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  dateRange,
  onDateRangeChange
}) => {
  const { currentArtistId, availableArtists, currentRoles, currentPermissions } = useArtist();
  const [currentArtist, setCurrentArtist] = useState<UserArtist | null>(null);
  const [loading, setLoading] = useState(true);
  const { userProfile } = useUser();
  const [dismissedMessages, setDismissedMessages] = useState<Set<string>>(new Set());

  // Get localStorage keys for dismissed messages (user-specific)
  const getDismissedMessagesKey = () => {
    const userId = userProfile?.artist?.[0]?.artist_id || userProfile?.id || 'default';
    return `dashboard-dismissed-messages-${userId}`;
  };

  // Load dismissed messages from localStorage
  useEffect(() => {
    if (userProfile) {
      const key = getDismissedMessagesKey();
      const stored = localStorage.getItem(key);
      if (stored) {
        try {
          const dismissed = JSON.parse(stored);
          setDismissedMessages(new Set(dismissed));
        } catch (_error) {
          // Invalid stored data, ignore
        }
      }
    } else {
      // Clear dismissed messages when user logs out
      setDismissedMessages(new Set());
    }
  }, [userProfile]);

  // Save dismissed messages to localStorage
  const saveDismissedMessages = (messages: Set<string>) => {
    if (userProfile) {
      const key = getDismissedMessagesKey();
      localStorage.setItem(key, JSON.stringify(Array.from(messages)));
    }
  };

  // Dismiss a message
  const dismissMessage = (messageId: string) => {
    const newDismissed = new Set(dismissedMessages);
    newDismissed.add(messageId);
    setDismissedMessages(newDismissed);
    saveDismissedMessages(newDismissed);
  };

  // Check if message is dismissed
  const isMessageDismissed = (messageId: string): boolean => {
    return dismissedMessages.has(messageId);
  };

  // Platform links mapping
  const platformLinks: { [key: string]: string } = {
    'YouTube': 'https://support.google.com/youtube/answer/7336634?hl=en#zippy=%2Chow-to-apply-for-an-official-artist-channel',
    'SoundCloud': 'https://help.soundcloud.com/hc/en-us/articles/360058973693-Profile-verification',
    'TikTok': 'https://artists.tiktok.com/help-center/artist-certification',
    'Instagram': 'https://help.instagram.com/2358103564437429?helpref=faq_content',
    'X (Twitter)': 'https://business.x.com/en/help/account-setup/professional-accounts',
    'Amazon Music': 'https://artists.amazonmusic.com/getting-started#to-claim-an-artist',
    'Deezer': 'https://creatorsupport.deezer.com/hc/en-us/articles/6328093844893-How-To-Get-Access-To-Deezer-For-Creators#request_access_as_an_artist',
    'Audiomack': 'https://audiomack.com/world/post/account-claim',
    'Pandora': 'https://community.pandora.com/t5/AMP-Support-Forum/How-Do-I-Claim-my-Artist-Profile/td-p/127745',
    'Tidal': 'https://support.tidal.com/hc/en-us/articles/15034795869201-TIDAL-Artist-Home-Enrollment'
  };

  // Check which social media links are missing
  const getMissingProfiles = () => {
    if (!userProfile?.artist?.[0]) return [];
    
    const artist = userProfile.artist[0];
    const missing = [];
    
    if (!artist.youtube_url) missing.push('YouTube');
    //if (!artist.apple_url) missing.push('Apple Music');
    if (!artist.instagram_url) missing.push('Instagram');
    //if (!artist.facebook_url) missing.push('Facebook');
    if (!artist.tiktok_url) missing.push('TikTok');
    if (!artist.soundcloud_url) missing.push('SoundCloud');
    if (!artist.twitter_url) missing.push('X (Twitter)');
    if (!artist.amazon_music_url) missing.push('Amazon Music');
    if (!artist.deezer_url) missing.push('Deezer');
    if (!artist.audiomack_url) missing.push('Audiomack');
    if (!artist.pandora_url) missing.push('Pandora');
    if (!artist.tidal_url) missing.push('Tidal');
    
    // Note: Spotify URL is assumed to be present as per the requirement
    
    return missing;
  };
  
  const hasMissingProfiles = () => {
    const missing = getMissingProfiles();
    return missing.length > 0;
  };

  // Load current artist for the dashboard heading when artist changes
  useEffect(() => {
    const loadCurrentArtist = async () => {
      if (!currentArtistId) {
        setLoading(false);
        setCurrentArtist(null);
        return;
      }

      try {
        setLoading(true);
        // Get user's artists to find the matching one
        const artistsResponse = await artistAnalyticsService.getUserArtists();
        const userArtist = artistsResponse.artists.find(artist => artist.id === currentArtistId.toString());
        
        if (userArtist) {
          setCurrentArtist(userArtist);
        } else {
          // Fallback: create from availableArtists
          const selectedArtist = availableArtists.find(a => a.artistId === currentArtistId);
          if (selectedArtist) {
            // Determine role from currentRoles or default to 'collaborator'
            const role = currentRoles && currentRoles.length > 0 
              ? (currentRoles[0] as 'owner' | 'manager' | 'collaborator') || 'collaborator'
              : (selectedArtist.isOwner ? 'owner' : 'collaborator');
            
            const artistObj: UserArtist = {
              id: selectedArtist.artistId.toString(),
              name: selectedArtist.artistName,
              role: role,
              isPrimary: selectedArtist.isOwner,
              permissions: currentPermissions || [],
              isDefault: false
            };
            setCurrentArtist(artistObj);
          }
        }
      } catch (_error) {
        setCurrentArtist(null);
      } finally {
        setLoading(false);
      }
    };

    loadCurrentArtist();
  }, [currentArtistId, availableArtists]); // Refetch when artist changes

  // Set default date range (6 months from today)
  useEffect(() => {
    const today = new Date();
    const sixMonthsAgo = new Date(today.getTime() - 180 * 24 * 60 * 60 * 1000);
    
    if (!dateRange.from || !dateRange.to) {
      onDateRangeChange(sixMonthsAgo, today);
    }
  }, []);

  const getDashboardTitle = () => {
    if (loading) return "Dashboard";
    // Priority 1: Selected artist name (from ArtistContext)
    const selectedName = currentArtist?.name ||
      availableArtists.find(a => a.artistId === currentArtistId)?.artistName;
    if (selectedName && selectedName.trim() !== '') {
      const nameParts = selectedName.split(' ').filter(part => part.trim() !== '');
      const capitalizedNames = nameParts.map(part => 
        part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
      );
      const fullName = capitalizedNames.join(' ');
      return `${fullName}'s Dashboard`;
    }

    // Priority 2: userProfile artist name (legacy fallback)
    const userProfileArtistName = userProfile?.artist?.[0]?.artist_name;
    if (userProfileArtistName && userProfileArtistName.trim() !== '') {
      const nameParts = userProfileArtistName.split(' ').filter(part => part.trim() !== '');
      const capitalizedNames = nameParts.map(part => 
        part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
      );
      const fullName = capitalizedNames.join(' ');
      return `${fullName}'s Dashboard`;
    }

    // Fallback: Default dashboard title
    return "Dashboard";
  };

  return (
    <div className="">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="profile-info-text-size text-black">{getDashboardTitle()}</h1>
      </div>
      
      {/* Show message when artist is not mapped - different copy if no Spotify URL vs URL pending */}
      {userProfile?.artist?.[0]?.is_artist_mapped === false && !isMessageDismissed('artist-not-mapped') && (
        <div className="mt-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 relative">
            <button
              onClick={() => dismissMessage('artist-not-mapped')}
              className="absolute top-2 right-2 text-blue-600 hover:text-blue-800 transition-colors"
              aria-label="Dismiss message"
            >
              <X size={18} />
            </button>
            <p className="text-sm-nova text-blue-800 pr-6">
              {!(userProfile?.artist?.[0]?.spotify_url ?? '').trim() ? (
                <>
                  Add your Spotify artist URL in{' '}
                  <Link to="/settings" className="text-blue-600 hover:underline font-medium">Settings</Link>
                  {' '}or{' '}
                  <Link to="/profile" className="text-blue-600 hover:underline font-medium">Profile</Link>
                  {' '}to see analytics and charts. You can still use your{' '}
                  <Link to="/banking" className="text-blue-600 hover:underline font-medium">Nova bank account</Link>
                  {' '}and join our{' '}
                  <a href={process.env.REACT_APP_NOVA_DISCARD_LINK} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">discord</a>
                  {' '}in the meanwhile.
                </>
              ) : (
                <>
                  Please check back in 7 business days. We are pulling your data from all Platforms.
                  Feel free to open a{' '}
                  <Link to="/banking" className="text-blue-600 hover:underline font-medium">Nova bank account</Link>
                  {' '}and join our{' '}
                  <a href={process.env.REACT_APP_NOVA_DISCARD_LINK} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">discord profile</a>
                  {' '}and update your{' '}
                  <Link to="/settings" className="text-blue-600 hover:underline font-medium">settings</Link>
                  {' '}in the meanwhile.
                </>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Show message when artist is mapped but has missing profile URLs */}
      {userProfile?.artist?.[0]?.is_artist_mapped !== false && hasMissingProfiles() && !isMessageDismissed('missing-profile-urls') && (
        <div className="mt-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 relative">
            <button
              onClick={() => dismissMessage('missing-profile-urls')}
              className="absolute top-2 right-2 text-yellow-600 hover:text-yellow-800 transition-colors"
              aria-label="Dismiss message"
            >
              <X size={18} />
            </button>
            <div className="pr-6">
              <p className="text-sm-nova text-yellow-800">
                We've identified your Spotify and Apple Music profiles.
              </p>
              <p className="text-sm-nova text-yellow-800">
                To unlock full analytics across all platforms, please claim your remaining official profiles from the channels below and link them in your{' '}
                <Link 
                  to="/profile" 
                  className="text-yellow-600 hover:underline font-medium"
                >
                  Profile page
                </Link>
                .
              </p>
              <p className="text-sm-nova text-yellow-800">
                This allows us to pull in real-time data for your dashboard.
              </p>
              <p className="text-sm-nova text-yellow-800 mt-2">
                How to claim your profiles on - {' '}
                {getMissingProfiles().map((platform, index) => {
                  const link = platformLinks[platform];
                  const isLast = index === getMissingProfiles().length - 1;
                  return (
                    <span key={platform}>
                      {link ? (
                        <>
                          <a 
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-yellow-600 hover:underline font-medium"
                          >
                            {platform}
                          </a>
                          {!isLast && ', '}
                        </>
                      ) : (
                        <>
                          <strong>{platform}</strong>
                          {!isLast && ', '}
                        </>
                      )}
                    </span>
                  );
                })}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardHeader;
